/**
 * Presentational companion-usage meter — "X% used / Y% available" over a
 * LinearProgress. Lives inside the ⋯ overflow menu (deliberately never
 * pinned in a header — premium surfaces don't lead with a fuel gauge; the
 * ambient AssistantUsageHint takes over at ≥80%). Percentage only — never
 * euros, credits, tokens, or the budget value (the "no quotas surfaced"
 * rule). Fetching lives in useAssistantUsage; callers hide the meter (and
 * any label around it) when the hook returns null.
 */

import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface AssistantUsageMeterProps {
  /** Clamped 0–100 percentage from useAssistantUsage. */
  value: number;
}

const AssistantUsageMeter = ({ value }: AssistantUsageMeterProps) => {
  const { t } = useTranslation();
  const used = Math.min(100, Math.max(0, Math.round(value)));

  return (
    <Box aria-label={t('assistant.usage.aria', { percent: used })}>
      <LinearProgress variant="determinate" value={used} sx={{ height: 6, borderRadius: 3 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {t('assistant.usage.used', { percent: used })}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {t('assistant.usage.available', { percent: 100 - used })}
        </Typography>
      </Stack>
    </Box>
  );
};

export default AssistantUsageMeter;
